import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../context/ToastContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Chip,
} from '@mui/material';
import { IconTrendingUp, IconTrendingDown } from '@tabler/icons-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { fetchComparativeAnalysis } from '../../slices/reportsSlice';

const MODULE_OPTIONS = [
  { value: 'financial', label: 'المالية' },
  { value: 'students', label: 'الطلاب' },
];

const ComparativeAnalysis = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { comparativeData, comparativeLoading } = useSelector(
    (state) => state.reports
  );

  const [filters, setFilters] = useState({
    year1: new Date().getFullYear() - 1,
    year2: new Date().getFullYear(),
    module: 'financial',
  });

  const handleFetch = () => {
    if (!filters.year1 || !filters.year2) {
      showToast('يرجى تحديد السنتين', 'error');
      return;
    }
    dispatch(fetchComparativeAnalysis(filters));
  };

  const prepareChartData = () => {
    if (!comparativeData) return [];

    const { year1_data, year2_data } = comparativeData;
    const keys = Object.keys(year1_data || {});

    return keys.map((key) => {
      const val1 = typeof year1_data[key] === 'object' 
        ? Object.values(year1_data[key])[0] || 0
        : year1_data[key] || 0;
      const val2 = typeof year2_data[key] === 'object'
        ? Object.values(year2_data[key])[0] || 0
        : year2_data[key] || 0;

      return {
        name: key,
        [`${filters.year1}`]: val1,
        [`${filters.year2}`]: val2,
      };
    });
  };

  const renderComparisonCards = () => {
    if (!comparativeData || !comparativeData.differences) return null;

    const differences = comparativeData.differences;
    const cards = [];

    Object.keys(differences).forEach((key) => {
      const diff = differences[key];
      if (typeof diff === 'object' && diff.absolute !== undefined) {
        const isPositive = diff.absolute >= 0;
        cards.push(
          <Card key={key}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {key}
              </Typography>
              <Stack useFlexGap direction="row" alignItems="center" spacing={1} mt={1}>
                {isPositive ? (
                  <IconTrendingUp color="#4caf50" />
                ) : (
                  <IconTrendingDown color="#f44336" />
                )}
                <Typography variant="h6">
                  {diff.absolute > 0 ? '+' : ''}
                  {diff.absolute.toFixed(2)}
                </Typography>
              </Stack>
              <Chip
                label={`${diff.percentage > 0 ? '+' : ''}${diff.percentage.toFixed(1)}%`}
                color={isPositive ? 'success' : 'error'}
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        );
      }
    });

    return cards;
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          التحليل المقارن (سنة مقابل سنة)
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="السنة الأولى"
              type="number"
              value={filters.year1}
              onChange={(e) =>
                setFilters({ ...filters, year1: parseInt(e.target.value) })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="السنة الثانية"
              type="number"
              value={filters.year2}
              onChange={(e) =>
                setFilters({ ...filters, year2: parseInt(e.target.value) })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>الوحدة</InputLabel>
              <Select
                value={filters.module}
                label="الوحدة"
                onChange={(e) =>
                  setFilters({ ...filters, module: e.target.value })
                }
              >
                {MODULE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Button
          variant="contained"
          onClick={handleFetch}
          disabled={comparativeLoading}
          sx={{ mt: 2 }}
        >
          {comparativeLoading ? <CircularProgress size={20} /> : 'تحليل'}
        </Button>
      </Paper>

      {comparativeLoading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {comparativeData && !comparativeLoading && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {renderComparisonCards()}
          </Grid>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              الرسم البياني المقارن
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={prepareChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey={`${filters.year1}`} fill="#4d4cac" />
                <Bar dataKey={`${filters.year2}`} fill="#4caf50" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ComparativeAnalysis;
